import { Check, Plus } from "lucide-react";
import { FOCUS_RING } from "./interactionStyles";
import { useI18n } from "@/lib/i18n/useI18n";

// ─── Selection action ─────────────────────────────────────────────────────────
// Shared add/remove control for marketplace cards/rows. Rendered only when the
// parent has wired local selection tracking — `isSelected` is `undefined` in
// contexts without it, so those paths render nothing extra.
//
// Marketplace Comparison V1 — "selección" was renamed to "comparar" across
// the marketplace: this is the control that adds/removes an item from the
// (max-3) comparison set, not a shopping cart or a Planner itinerary draft.
export function SelectionAction({
  isSelected,
  onAdd,
  onRemove,
  itemTitle,
  addLabel,
}: {
  isSelected?: boolean;
  onAdd?:      () => void;
  onRemove?:   () => void;
  itemTitle:   string;
  /** Override the add-state label for a surface with different emphasis
   *  (e.g. a taller primary button). Same state/handlers either way. */
  addLabel?:   string;
}) {
  const { t } = useI18n();
  if (isSelected === undefined) return null;

  if (isSelected) {
    return (
      // Marketplace Interaction Polish V1, task 3 — a brief pop-in on the
      // moment this state first mounts (right after "Añadir a comparar" is
      // clicked and this branch replaces that one) is the feedback that
      // tells the user the click actually registered, not just a silent
      // swap. animate-in only ever plays once per mount, never loops.
      <div className="inline-flex items-center gap-1.5 animate-in fade-in-0 zoom-in-95 duration-200 motion-reduce:animate-none">
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold"
          style={{ color: "oklch(0.46 0.12 145)" }}
        >
          <Check size={11} aria-hidden />
          {t("marketplace.compare.added")}
        </span>
        {onRemove && (
          // Marketplace Inventory Scale & Discovery V1, task 9 — 44x44
          // minimum touch target, everywhere this control appears (row,
          // shelf, drawer, tray).
          <button
            type="button"
            onClick={onRemove}
            aria-label={t("marketplace.compare.removeAria", { itemTitle })}
            className={`inline-flex items-center justify-center text-[10px] font-semibold px-2.5 rounded-full transition-all duration-200 hover:opacity-80 active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100 ${FOCUS_RING}`}
            style={{
              minWidth:   "44px",
              minHeight:  "44px",
              color:      "oklch(0.545 0.020 60 / 0.60)",
              background: "oklch(0.545 0.020 60 / 0.06)",
              border:     "1px solid oklch(0.545 0.020 60 / 0.22)",
            }}
          >
            {t("marketplace.compare.remove")}
          </button>
        )}
      </div>
    );
  }

  // Marketplace Inventory Scale & Discovery V1, task 9 — 44px minimum
  // height for the primary add action, everywhere this control appears.
  return onAdd ? (
    <button
      type="button"
      onClick={onAdd}
      aria-label={t("marketplace.compare.addAria", { itemTitle })}
      className={`inline-flex items-center gap-1 text-[11px] font-bold px-3 rounded-full transition-all duration-200 hover:opacity-90 active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 animate-in fade-in-0 duration-200 motion-reduce:animate-none ${FOCUS_RING}`}
      style={{
        minHeight:  "44px",
        color:      "#FFFFFF",
        background: "#E8194B",
      }}
    >
      <Plus size={11} aria-hidden />
      {addLabel ?? t("marketplace.compare.add")}
    </button>
  ) : null;
}